import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import moment from "moment";
import { useSelector } from "react-redux";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { value, date } = payload[0].payload;
    return (
      <div
        style={{
          padding: "10px",
          color: "#333",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Value:</strong>
          <p style={{ color: "#333" }}>{value}</p>
        </div>
        <div className="flex gap-2">
          <strong style={{ margin: 0, color: "#333" }}>Date:</strong>
          <p style={{ color: "#333" }}>{moment(date).format("DD-MM-YYYY")}</p>
        </div>
      </div>
    );
  }
  return null;
};

const ReturnComponent = ({
  graphROI,
  selectedAsset,
  mediaData,
  mediaLoading,
  graphLoading,
}) => {
  const processWaterfall = (data) => {
    let cumulative = 0;
    let initial = 0;
    let total = 0;

    return data.map((d) => {
      let delta = parseFloat(d.delta || d.value || 0);
      let base = total;
      let color = "positive"; // default

      if (d.is_initial) {
        // Initial value
        cumulative = d.value;
        initial = d.value;
        total = d.value;
        delta = d.value;
        color = "positive";
      } else if (d.type === "INCOME" || d.type === "IN" || d.type === "BUY") {
        cumulative += delta;
        total += Math.abs(d.value) || d.value;
        color = "positive";
      } else if (d.type === "EXPENSE" || d.type === "SELL") {
        delta = -Math.abs(delta);
        cumulative += delta;
        total -= Math.abs(d.value);
        color = "negative";
      } else if (d.type === "VALUATION") {
        delta = d.value - total;
        total += delta;
        cumulative = delta;
        color = delta >= 0 ? "positive" : "negative";
      }

      return {
        label: d.type,
        base,
        delta,
        value: total,
        date: d.date,
        color,
      };
    });
  };

  const data = processWaterfall(graphROI || []);

  const theme = useSelector((state) => state.theme.mode);
  const { userAssetLoading } = useSelector((state) => state.userAssets);

   const formatTick = (value) => {
    const format = (num, suffix) => {
      let str = num.toFixed(1);
      if (str.endsWith(".0")) str = str.slice(0, -2);
      return str + suffix;
    };

    if (value >= 1e12 || value <= -1e12) {
      return format(value / 1e12, "T");
    } else if (value >= 1e9 || value <= -1e9) {
      return format(value / 1e9, "B");
    } else if (value >= 1e6 || value <= -1e6) {
      return format(value / 1e6, "M");
    } else if (value >= 1e3 || value <= -1e3) {
      return format(value / 1e3, "K");
    }
    return value.toString();
  };

  return (
    <div className="lg:flex gap-16 md:space-y-0 space-y-5 mt-8">
      <div className="px-5 pb-5 pt-3 border border-lightPrimary/40 dark:border-info/40 dark:bg-custom-radial bg-white lg:w-3/5 w-full">
        {graphLoading || userAssetLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mx-auto"></div>
            <div className="h-[30rem] bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ) : (
          <>
            <h2 className="text-2xl text-lightPrimary dark:text-primary-gradient font-semibold pl-1 pb-3 text-center">
              {selectedAsset?.name
                ? `${selectedAsset?.name}`
                : "No Asset found"}
            </h2>

            {data.length > 0 && (
              <ResponsiveContainer
                width="100%"
                height={500}
                className={
                  data.length <= 2 ? "mx-0 xl:mx-40 mt-8" : `mx-0 xl:mx-10`
                }
                style={{
                  maxWidth: data.length <= 2 ? `${data.length * 240}px` : "80%",
                }}
              >
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                  barCategoryGap={0}
                  barGap={0}
                >
                  <CartesianGrid strokeDasharray="0 3" />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    tick={{
                      fill: theme === "dark" ? "#cecece" : "#a5a5a8",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: theme === "dark" ? "#cecece" : "#a5a5a8",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                    tickFormatter={formatTick}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={false} />
                  <Bar dataKey="base" stackId="a" fill="transparent" />

                  {/* Delta bar */}
                  <Bar
                    dataKey="delta"
                    stackId="a"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={data.length <= 2 ? 200 : undefined}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.color === "negative"
                            ? "url(#negativeGradient)"
                            : "url(#positiveGradient)"
                        }
                      />
                    ))}
                  </Bar>

                  {/* Gradients */}
                  <defs>
                    <linearGradient
                      id="positiveGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#d4e3eeff"
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="90%"
                        stopColor="#6dbee4ff"
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="105%"
                        stopColor="#b7dcfaff"
                        stopOpacity={0.8}
                      />
                    </linearGradient>

                    <linearGradient
                      id="negativeGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#e7c898" stopOpacity={0.9} />
                      <stop
                        offset="95%"
                        stopColor="#EA8100"
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="105%"
                        stopColor="#f19019"
                        stopOpacity={0.9}
                      />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </div>

      {/* Right side media overview */}
      <div className="relative dark:bg-custom-radial overflow-x-auto lg:w-2/5 w-full border border-lightPrimary/40 dark:border-info/40 bg-white !mt-7 lg:!mt-0">
        <h2
          className={`${"text-lightPrimary dark:text-primary-gradient"} text-2xl font-semibold py-2 pl-4 inline-block`}
        >
          Overview
        </h2>
        {mediaLoading || userAssetLoading ? (
          <div className="animate-pulse space-y-4 p-5">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700"></div>
            <div className="flex justify-center">
              <div className="h-[27rem] bg-gray-200 dark:bg-gray-700 w-full max-w-md object-fill shadow-md"></div>
            </div>
          </div>
        ) : mediaData?.at(-1)?.url ? (
          <div className="text-lightPrimary dark:text-primary-gradient p-5 space-y-8">
            <div className="mt-7 flex items-center gap-2">
              <span className="text-info dark:text-grey">📌</span>
              <span>
                {mediaData
                  ?.at(-1)
                  ?.name?.substring(
                    0,
                    mediaData?.at(-1)?.name?.lastIndexOf(".")
                  ) || mediaData?.at(-1)?.name}
              </span>
            </div>
            <div className="flex justify-center">
              <img
                src={mediaData?.at(-1)?.url}
                alt={mediaData?.at(-1)?.name}
                className="w-full max-w-md object-fill shadow-md max-h-[27rem]"
              />
            </div>
          </div>
        ) : (
          <div className="p-5 flex flex-col items-center text-center text-red-500 justify-center gap-3 h-[30rem]">
            🚫 No image found
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnComponent;
